import {
  Box,
  Button,
  CardContent,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { AnimalsIcon, DryMatterIcon, FieldsIcon } from "common/icons";
import { useTranslation } from "common/locales";
import AssignedToDropdown from "features/fieldEdit/AssignedToDropdown";
import {
  Field,
  getTotalAnimalCount,
  selectAllAnimalGroups,
  selectAnimalGroupById,
  useAppSelector,
} from "model";
import { getAvailableDryMatterForAnimalGroupId } from "model/fieldSlice";
import { useState } from "react";
import styled from "styled-components";

interface LivestockSectionProps {
  field: Field;
  onGroupChange: (guid?: string) => void;
}

const AnimalCard = styled(Box)`
  cursor: pointer;
  transition: transform 0.2s;
  background-color: white;
  border-radius: 16px;
  border: 1px solid #e0e0e0;
  margin-left: 16px;
  margin-right: 16px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  }
`;

const IconCircle = styled.div<{ color: string }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: ${({ color }) => color};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  margin-right: 12px;
`;

const MetricRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`;

const MetricsContainer = styled.div`
  margin-top: 16px;
`;

const MetricColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const AssignButton = styled(Button)`
  color: #2e7831;
  text-transform: none;
  font-weight: 500;
`;

export default function LivestockSection({
  field,
  onGroupChange,
}: LivestockSectionProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const iconSize = isMobile ? "small" : "medium";
  const [showAssignDialog, setShowAssignDialog] = useState(false);

  const animalGroups = useAppSelector(selectAllAnimalGroups);
  const currentAnimalGroup = useAppSelector((state) =>
    field.animalGroup ? selectAnimalGroupById(state, field.animalGroup) : null
  );

  // Get the total animal count for the current animal group
  const animalCount = useAppSelector((state) =>
    currentAnimalGroup?.guid
      ? getTotalAnimalCount(state, currentAnimalGroup.guid) || 0
      : 0
  );

  // Get the available dry matter for the current animal group
  const dryMatterAvailable = useAppSelector((state) =>
    currentAnimalGroup?.guid
      ? getAvailableDryMatterForAnimalGroupId(currentAnimalGroup.guid)(state) ||
        0
      : 0
  );

  const handleAssignNewGroup = () => {
    setShowAssignDialog(true);
  };

  const handleGroupChange = (guid?: string) => {
    // This will be handled by the parent component
    setShowAssignDialog(false);
    onGroupChange(guid);
  };

  if (animalGroups.length === 0) return null;

  return (
    <Container sx={{ pb: 3 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
          px: 2,
          pt: 2,
        }}
      >
        <Typography
          sx={{
            fontWeight: 600,
            color: "text.primary",
          }}
        >
          {t("fieldview.livestock.title")}
        </Typography>
        <AssignButton
          variant="text"
          onClick={handleAssignNewGroup}
          startIcon={<AnimalsIcon fontSize="small" />}
        >
          {t("fieldview.livestock.assignNewGroup")}
        </AssignButton>
      </Box>

      {currentAnimalGroup ? (
        <AnimalCard>
          <CardContent>
            <MetricRow>
              <IconCircle color={currentAnimalGroup.color || "#9C27B0"}>
                <AnimalsIcon fontSize="small" />
              </IconCircle>
              <Typography variant="body1" fontWeight="medium" gutterBottom>
                {currentAnimalGroup.name}
              </Typography>
            </MetricRow>

            <MetricsContainer>
              <MetricColumn>
                <MetricRow>
                  <FieldsIcon fontSize={iconSize} />
                  <Typography variant="body2" sx={{ color: "text.primary" }}>
                    {field.name}
                  </Typography>
                </MetricRow>

                <MetricRow>
                  <DryMatterIcon fontSize={iconSize} />
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{ color: "text.primary", fontWeight: 500 }}
                    >
                      {Math.round(dryMatterAvailable)} kgDM
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary", fontSize: "0.875rem" }}
                    >
                      {t("fieldview.livestock.dryMatterAvailable")}
                    </Typography>
                  </Box>
                </MetricRow>

                <MetricRow>
                  <AnimalsIcon fontSize={iconSize} />
                  <Typography variant="body2" sx={{ color: "text.primary" }}>
                    {animalCount} {t("fieldDetail.animals")}
                  </Typography>
                </MetricRow>
              </MetricColumn>
            </MetricsContainer>
          </CardContent>
        </AnimalCard>
      ) : (
        <Box
          sx={{
            textAlign: "center",
            py: 4,
            color: "text.secondary",
            backgroundColor: "white",
            borderRadius: 2,
            border: "2px dashed",
            borderColor: "divider",
          }}
        >
          <Typography variant="body1" gutterBottom>
            {t("fieldview.livestock.noAnimalGroupAssigned")}
          </Typography>
          <Typography variant="body2">
            {t("fieldview.livestock.assignAnimalGroupDescription")}
          </Typography>
        </Box>
      )}

      {/* Assign New Group Dialog */}
      <Dialog
        open={showAssignDialog}
        onClose={() => setShowAssignDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Assign Animal Group</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <AssignedToDropdown
              assignedTo={field.animalGroup}
              onChange={handleGroupChange}
            />
          </Box>
        </DialogContent>
      </Dialog>
    </Container>
  );
}
